import { useEffect, useState } from "react";
import { AlertTriangle, CircleAlert, X } from "lucide-react";
import { useBuildingStorageQuota } from "@/hooks/buildings/useBuildingStorageQuota";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { BuildingStorageQuotaError } from "../services/buildingStorageQuota";
import { availableStorageLabel, storageCopy, storageState } from "../utils/storagePresentation";
import { StorageActions } from "./StorageActions";
import { cn } from "@/lib/utils";

function bannerHidden(
  quota: ReturnType<typeof useBuildingStorageQuota>,
  state: ReturnType<typeof storageState>,
  key: string
) {
  if (!quota.data || quota.error instanceof BuildingStorageQuotaError) return true;
  if (quota.data.activationPending) return true;
  if (state === "normal" || state === "unlimited") return true;
  return state === "near" && sessionStorage.getItem(key) === "dismissed";
}

export function StorageCriticalBanner({
  buildingId,
  orgId,
}: {
  buildingId: string;
  orgId?: string;
}) {
  const quota = useBuildingStorageQuota(buildingId, orgId);
  const { language } = useLanguage();
  const words = storageCopy[language];
  const key = `storage-banner:${orgId}:${buildingId}`;
  const [, renderDismissal] = useState(0);
  const state = quota.data ? storageState(quota.data) : "normal";
  useEffect(() => {
    if (!quota.data || quota.error instanceof BuildingStorageQuotaError) return;
    if (state !== "near") {
      sessionStorage.removeItem(key);
    }
  }, [key, state, quota.data, quota.error]);
  if (bannerHidden(quota, state, key)) return null;
  const full = state === "full";
  const Icon = full ? CircleAlert : AlertTriangle;
  const message = (full ? words.fullBanner : words.nearBanner).replace(
    "{remaining}",
    availableStorageLabel(quota.data)
  );
  return (
    <div className="mb-[16px] @container/storage-banner">
      <div
        role="alert"
        data-testid="storage-critical-banner"
        className={cn(
          "flex min-h-[46px] flex-wrap items-center gap-x-[12px] gap-y-[8px] rounded-lg border px-3 py-[5px] text-sm @lg/storage-banner:flex-nowrap",
          full
            ? "border-destructive/30 bg-destructive/5 text-destructive"
            : "border-amber-600/30 bg-amber-500/10 text-amber-800 dark:text-amber-300"
        )}
      >
        <Icon className="size-4 shrink-0" aria-hidden="true" />
        <p
          className="min-w-0 flex-1 basis-[calc(100%-2rem)] truncate @lg/storage-banner:basis-auto"
          title={message}
        >
          {message}
        </p>
        <StorageActions buildingId={buildingId} actionId="get_more_space_banner" variant="banner" />
        {!full && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-[30px] shrink-0"
            aria-label={words.dismiss}
            onClick={() => {
              sessionStorage.setItem(key, "dismissed");
              renderDismissal((value) => value + 1);
            }}
          >
            <X className="size-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
